import { useEffect, useState } from 'react'

/// wstETH earns staking yield by exchange-rate appreciation rather than as a supply APY, so the
/// rate is invisible to everything the pool measures (interest legs, HORIZON emissions) while being
/// entirely real to the position - the whole point of the correlated LST loop. The number comes
/// from Lido's own published 7-day SMA; when that endpoint is unreachable the fallback below keeps
/// the loop honest rather than showing the 0.00% that undersold it (the fallback is a real
/// long-run figure, and the UI labels the yield as an estimate either way).
const LIDO_APR_URL = 'https://eth-api.lido.fi/v1/protocol/steth/apr/sma'
const FALLBACK_APR_BPS = 270

/// Sanity bounds: Lido has historically paid 2.5-7%. A response outside 0.5%-15% is more likely a
/// schema change than a real rate, and the fallback is safer than trusting it.
const MIN_SANE_BPS = 50
const MAX_SANE_BPS = 1500

let cached = null // module-level: one fetch per app session, every hook instance shares it

export function useLstApr() {
  const [aprBps, setAprBps] = useState(cached ?? FALLBACK_APR_BPS)

  useEffect(() => {
    if (cached != null) return
    let alive = true
    fetch(LIDO_APR_URL)
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        const pct = json?.data?.smaApr
        const bps = typeof pct === 'number' ? Math.round(pct * 100) : null
        cached = bps != null && bps >= MIN_SANE_BPS && bps <= MAX_SANE_BPS ? bps : FALLBACK_APR_BPS
        if (alive) setAprBps(cached)
      })
      .catch(() => {
        cached = FALLBACK_APR_BPS
        if (alive) setAprBps(cached)
      })
    return () => { alive = false }
  }, [])

  return { aprBps }
}

/// Which reserves accrue intrinsic exchange-rate yield, and at what rate. WBTC is deliberately
/// absent: it has no native yield, so its loop number is complete without an entry here.
export function intrinsicAprBpsFor(reserveKey, lstAprBps) {
  return reserveKey === 'wsteth' ? lstAprBps : 0
}
